import { ApiService } from "Services/ApiService";
import {
  BannerAdminRequestDTO,
  BannerMstDTO,
  BannerWithoutBytesDTO,
  ResultsDTO,
  UpdatedBanner
} from "Types";



export const bannerApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getAdminBanners: builder.query<ResultsDTO<BannerMstDTO[]>, void>({
      query: () => ({
        url: "admin/banners",
        method: "GET",
      }),
      providesTags: [{ type: "Banner", id: "LIST" }],
    }),

    getAllBannersWithoutBytes: builder.query<ResultsDTO<BannerWithoutBytesDTO[]>, void>({
      query: () => ({
        url: "admin/banners/allbanner",
        method: "GET",
        headers: {
          origin: "origin",
        },
      }),
      providesTags: [{ type: "Banner", id: "ALL_BANNERS" }],
    }),

    getBannerById: builder.query<ResultsDTO<BannerMstDTO>, number>({
      query: (id) => ({
        url: `admin/banners/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Banner", id }],
    }),

    createBanner: builder.mutation<ResultsDTO, BannerAdminRequestDTO>({
      query: (data) => ({
        url: "admin/banners/create",
        method: "POST",
        body: data,
        headers: {
          origin: "origin",
        },
      }),
      invalidatesTags: () => [{ type: "Banner", id: "LIST" }, { type: "Banner", id: "MAX_LNNO" }, { type: "Banner", id: "ALL_BANNERS" }],
    }),

    updateBanner: builder.mutation<ResultsDTO, { id: number; data: Partial<UpdatedBanner> }>({
      query: ({ id, data }) => ({
        url: `admin/banners/update/${id}`,
        method: "PUT",
        body: data,
        headers: {
          origin: "origin",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Banner", id },
        { type: "Banner", id: "LIST" },
      ],
    }),

    deleteBanner: builder.mutation<ResultsDTO, number>({
      query: (id) => ({
        url: `admin/banners/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Banner", id: "LIST" }, { type: "Banner", id: "MAX_LNNO" }, { type: "Banner", id: "ALL_BANNERS" }],
    }),

    uploadBannerImage: builder.mutation<ResultsDTO, { id: number; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `admin/banners/upload/${id}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Banner", id },
        { type: "Banner", id: "LIST" },
      ],
    }),

    uploadBanner2Image: builder.mutation<ResultsDTO, { id: number; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/admin/banners/upload2/${id}`,
          method: "POST",
          body: formData,
          headers: {
            origin: "origin",
          },
        };
      },
    }),

    deleteBannerImage: builder.mutation<ResultsDTO, number>({
      query: (id) => ({
        url: `admin/banners/delete-image/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Banner", id },
        { type: "Banner", id: "LIST" },
      ],
    }),

    deleteBanner2Image: builder.mutation<ResultsDTO, number>({
      query: (id) => ({
        url: `admin/banners/delete-image2/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Banner", id },
        { type: "Banner", id: "LIST" },
      ],
    }),

    bulkDeleteBanners: builder.mutation<ResultsDTO, { ids: number[] }>({
      query: ({ ids }) => ({
        url: "admin/banners/bulk-delete",
        method: "DELETE",
        params: { ids },
        headers: {
          origin: "origin",
        },
      }),
      invalidatesTags: () => [{ type: "Banner", id: "LIST" }, { type: "Banner", id: "MAX_LNNO" }, { type: "Banner", id: "ALL_BANNERS" }],
    }),

    getMaxBannerLnNo: builder.query<ResultsDTO<number>, void>({
      query: () => ({
        url: "admin/banners/max-lnno",
        method: "GET",
        headers: {
          origin: "origin",
        },
      }),
      providesTags: [{ type: "Banner", id: "MAX_LNNO" }],
    }),

    updateBannerOrder: builder.mutation<ResultsDTO, { id: number; lnNo: number }[]>({
      query: (data) => ({
        url: "admin/banners/order",
        method: "PUT",
        body: data,
        headers: {
          origin: "origin",
        },
      }),
      invalidatesTags: () => [{ type: "Banner", id: "ALL_BANNERS" }, { type: "Banner", id: "LIST" }, { type: "Banner", id: "MAX_LNNO" }],
    }),
  }),
});

export const {
  useGetAdminBannersQuery,
  useGetAllBannersWithoutBytesQuery,
  useGetBannerByIdQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
  useUploadBannerImageMutation,
  useUploadBanner2ImageMutation,
  useDeleteBannerImageMutation,
  useDeleteBanner2ImageMutation,
  useBulkDeleteBannersMutation,
  useGetMaxBannerLnNoQuery,
  useUpdateBannerOrderMutation,
} = bannerApiSlice;
